import { tool, type Tool } from "ai";

const TOOL_LIFECYCLE = Symbol.for("cc.projectToolLifecycle");

export type ProjectToolLifecycle = {
  continueAfterTool: boolean;
};

type ToolWithLifecycle<T extends Tool<any, any>> = T &
  Record<typeof TOOL_LIFECYCLE, ProjectToolLifecycle>;

export function projectTool<T extends Tool<any, any>>(
  args: T & { lifecycle: ProjectToolLifecycle },
): ToolWithLifecycle<T> {
  const { lifecycle, ...toolArgs } = args;
  const rawToolArgs: unknown = toolArgs;
  return Object.assign(tool(rawToolArgs as T), {
    [TOOL_LIFECYCLE]: lifecycle,
  }) as ToolWithLifecycle<T>;
}

export function getProjectToolLifecycle(
  toolDefinition: Record<typeof TOOL_LIFECYCLE, ProjectToolLifecycle>,
): ProjectToolLifecycle;
export function getProjectToolLifecycle(toolDefinition: unknown): ProjectToolLifecycle | null;
export function getProjectToolLifecycle(toolDefinition: unknown): ProjectToolLifecycle | null {
  return readProjectToolLifecycle(toolDefinition);
}

function readProjectToolLifecycle(toolDefinition: unknown): ProjectToolLifecycle | null {
  if (!toolDefinition || typeof toolDefinition !== "object") {
    return null;
  }

  const lifecycle = (toolDefinition as Record<symbol, unknown>)[TOOL_LIFECYCLE];
  if (!lifecycle || typeof lifecycle !== "object") {
    return null;
  }

  const continueAfterTool = (lifecycle as { continueAfterTool?: unknown }).continueAfterTool;
  if (typeof continueAfterTool !== "boolean") {
    return null;
  }

  return { continueAfterTool };
}

export function getToolContinuationPolicy(
  toolDefinition: unknown,
  continueGeneration: boolean,
): boolean {
  return readProjectToolLifecycle(toolDefinition)?.continueAfterTool ?? continueGeneration;
}
